import React, { useContext, useState } from 'react'
import { Dialog } from '../../components/Dialog'
import { Link } from 'react-router'
import { CommonAssortmentWithCountForStatusTypes, CommonsWithAssortmentConflicts } from 'domain/DTO'
import { CommonAssortmentStatusCell } from 'components/CommonAssortmentStatusCell'
import {
  allUiStatuses,
  fileTypesForCommonAssortmentWithCountForStatusTypes,
  getStatusEmoji,
  getStatusText,
  mapBackendStatusToUiStatus,
  UiStatus,
} from 'domain/FileStatus'
import { DuplicateCommonAssortmentDialog } from 'components/DuplicateCommonAssortmentDialog'
import { CommonAssortmentFields } from 'components/CommonAssortmentDialog'
import { deleteCommonAssortment, duplicateCommonAssortment } from 'services/CommonAssortmentService'
import { ConfirmDeletionDialog } from 'components/ConfirmDeletionDialog'
import { SortableHeaderContent, useTableSorting } from 'components/assortment/TableColumnSort'
import { padNumberToString } from 'lib/StringUtilities'
import { SelectedCountryContext } from 'domain/SelectedCountryContext'
import { ConflictSummary } from '../../components/ConflictSummary'
import { SRD } from 'srd'
import { CopyIcon, DeleteIcon } from '../../icons'
import SearchIcon from '../../icons/SearchIcon'
import { hasViewAssortmentPermission } from '../../lib/Permission'
import { useAutoRefresh, useRemoteData } from '../../lib/RemoteData'
import { API_HOST } from '../../lib'
import { RefreshCheckBox } from '../../components/RefreshCheckBox'
import { PagedTable } from '@tomra/react-table'
import { LoadingSpinner } from 'components/LoadingSpinner'

const CommonAssortmentOverview = () => {
  const [duplicateTarget, setDuplicateTarget] = useState<CommonAssortmentWithCountForStatusTypes | undefined>()
  const [deletionCandidate, setDeleteCandidate] = useState<[string, string] | undefined>()
  const { selectedCountry } = useContext(SelectedCountryContext)

  const { data: commonsWithAssortmentConflicts, refresh } = useRemoteData<CommonsWithAssortmentConflicts>(
    `${API_HOST}/bff/commons?country=${selectedCountry}`,
  )

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState<boolean>(false)

  const refreshIntervalInSeconds = 180
  useAutoRefresh(refresh, refreshIntervalInSeconds * 1000, autoRefreshEnabled)

  const sorting = useTableSorting<CommonAssortmentWithCountForStatusTypes>(
    {
      Name: (cc) => cc.name,
      '# Machines': (cc) => padNumberToString(cc.installationCount ?? 0, '00000'),
    },
    'Name',
  )

  return (
    hasViewAssortmentPermission() &&
    SRD.match(
      {
        notAsked: () => <LoadingSpinner />,
        loading: () => <LoadingSpinner />,
        failure: (err) => (
          <div className="centerAbsolute">
            <div className="alert alert-danger">
              Uh-oh.. Failed to load Common Assortments
              <div>{err.body?.title}</div>
            </div>
          </div>
        ),
        success: (commonWithAssortmentConflict) => {
          const statusElements = [
            'Shape Status',
            'Barcode Status',
            'Crate Status',
            'Local Crate Status',
            ...(selectedCountry === 'DE' ? ['Artikeldaten Status'] : []),
            'Actions',
          ]
          return (
            <React.Fragment>
              {!commonWithAssortmentConflict.commonAssortmentConflicts?.length ? null : (
                <ConflictSummary commonAssortmentConflict={commonWithAssortmentConflict.commonAssortmentConflicts} />
              )}
              <div className="card">
                <div className="inline-flex float-left items-center space-x-2">
                  <Link to="/common-assortment/new" className="btn btn-primary-dark inline py-sm">
                    Create Common Assortment
                  </Link>
                  <div className="p-md">
                    <RefreshCheckBox
                      checked={autoRefreshEnabled ?? false}
                      onToggle={setAutoRefreshEnabled}
                      title={`Refresh every ${refreshIntervalInSeconds} seconds`}
                    />
                  </div>
                </div>
                <div className="float-right mr-xs mb-sm">
                  {allUiStatuses.map((uiStatus) => {
                    return (
                      <span key={uiStatus} className="mr-md">
                        {getStatusEmoji(uiStatus) + ' ' + getStatusText(uiStatus)}
                      </span>
                    )
                  })}
                </div>
                <PagedTable
                  data={sorting.sort(commonWithAssortmentConflict.commons)}
                  columnHeaders={[
                    <div style={{ minWidth: '16em' }} key="name-header">
                      <SortableHeaderContent name="Name" currentSort={sorting.currentSort} />
                    </div>,
                    <div style={{ minWidth: '3em' }} key="lockout-header" />,
                    <div style={{ minWidth: '16em' }} key="maintenance-window-header">
                      Maintenance Window
                    </div>,
                    <div style={{ minWidth: '10em' }} key="serial-number-header">
                      <SortableHeaderContent name="# Machines" currentSort={sorting.currentSort} />
                    </div>,
                    ...statusElements,
                  ]}
                  rowFactory={(commonAssortmentWithCountForStatusTypes) => {
                    const fileTypes = fileTypesForCommonAssortmentWithCountForStatusTypes(
                      commonAssortmentWithCountForStatusTypes,
                    )

                    const link = `/common-assortment/${commonAssortmentWithCountForStatusTypes.commonAssortmentId}`
                    return (
                      <tr
                        key={commonAssortmentWithCountForStatusTypes.commonAssortmentId}
                        className="even:bg-grey-light hover:bg-fog"
                      >
                        <td>
                          <Link to={link} className="link">
                            {commonAssortmentWithCountForStatusTypes.name}
                          </Link>
                        </td>
                        <td>
                          {commonAssortmentWithCountForStatusTypes.lockout ? (
                            <span title="Common is locked, will not create new machine updates">{'🔒'}</span>
                          ) : (
                            ''
                          )}
                        </td>
                        <td>
                          {`${commonAssortmentWithCountForStatusTypes.maintenanceWindow.start} - ${commonAssortmentWithCountForStatusTypes.maintenanceWindow.end} (${commonAssortmentWithCountForStatusTypes.maintenanceWindow.tz})`}
                        </td>
                        <td>
                          <Link
                            to={`/installations?commonId=${commonAssortmentWithCountForStatusTypes.commonAssortmentId}&assortmentType=${fileTypes.join(',')}`}
                            className="inline-flex"
                          >
                            <span className="mr-sm link">
                              {commonAssortmentWithCountForStatusTypes.installationCount}
                            </span>
                            <SearchIcon color="none" size="18" />
                          </Link>
                        </td>
                        <td>
                          {fileTypes.includes('SHAPE') ? (
                            <CommonAssortmentStatusCell
                              statusCounts={statusCountsForType(
                                'SHAPE',
                                commonAssortmentWithCountForStatusTypes.commonAssortmentId,
                                commonAssortmentWithCountForStatusTypes.statusTypes,
                              )}
                            />
                          ) : (
                            ''
                          )}
                        </td>
                        <td>
                          {fileTypes.includes('BARCODE') ? (
                            <CommonAssortmentStatusCell
                              statusCounts={statusCountsForType(
                                'BARCODE',
                                commonAssortmentWithCountForStatusTypes.commonAssortmentId,
                                commonAssortmentWithCountForStatusTypes.statusTypes,
                              )}
                            />
                          ) : (
                            ''
                          )}
                        </td>
                        <td>
                          {fileTypes.includes('CRATE') ? (
                            <CommonAssortmentStatusCell
                              statusCounts={statusCountsForType(
                                'CRATE',
                                commonAssortmentWithCountForStatusTypes.commonAssortmentId,
                                commonAssortmentWithCountForStatusTypes.statusTypes,
                              )}
                            />
                          ) : (
                            ''
                          )}
                        </td>
                        <td>
                          {fileTypes.includes('LOCAL_CRATE') ? (
                            <CommonAssortmentStatusCell
                              statusCounts={statusCountsForType(
                                'LOCAL_CRATE',
                                commonAssortmentWithCountForStatusTypes.commonAssortmentId,
                                commonAssortmentWithCountForStatusTypes.statusTypes,
                              )}
                            />
                          ) : (
                            ''
                          )}
                        </td>
                        {selectedCountry === 'DE' && (
                          <td>
                            {fileTypes.includes('ARTIKELDATEN') ? (
                              <CommonAssortmentStatusCell
                                statusCounts={statusCountsForType(
                                  'ARTIKELDATEN',
                                  commonAssortmentWithCountForStatusTypes.commonAssortmentId,
                                  commonAssortmentWithCountForStatusTypes.statusTypes,
                                )}
                              />
                            ) : (
                              ''
                            )}
                          </td>
                        )}
                        <td>
                          <button
                            className="btn btn-icon inline-block mx-sm"
                            title="Duplicate"
                            onClick={() => setDuplicateTarget(commonAssortmentWithCountForStatusTypes)}
                          >
                            <CopyIcon />
                          </button>
                          <button
                            className="btn btn-icon inline-block mx-sm"
                            title="Remove"
                            onClick={() =>
                              setDeleteCandidate([
                                commonAssortmentWithCountForStatusTypes.name,
                                commonAssortmentWithCountForStatusTypes.commonAssortmentId,
                              ])
                            }
                          >
                            <DeleteIcon />
                          </button>
                        </td>
                      </tr>
                    )
                  }}
                  initialRecordsPerPage={100}
                  recordsPerPageOptions={[10, 20, 50, 100]}
                />
                {duplicateTarget ? (
                  <Dialog onClose={() => setDuplicateTarget(undefined)} shouldCloseOnOverlayClick={false}>
                    <DuplicateCommonAssortmentDialog
                      duplicatingFrom={duplicateTarget}
                      initialScopeFilters={duplicateTarget.scopeFilters}
                      onComplete={async ({ name, installationScopes, scopeFilters }) => {
                        const fields: CommonAssortmentFields = {
                          id: undefined,
                          name,
                          installationScopes,
                          scopeFilters,
                          country: selectedCountry,
                          maintenanceWindow: duplicateTarget.maintenanceWindow,
                          barcodeStreamId: duplicateTarget.barcodeFileStream?.id,
                          barcode2StreamId: duplicateTarget.barcode2FileStream?.id,
                          crateStreamId: duplicateTarget.crateFileStream?.id,
                          shapeStreamId: duplicateTarget.shapeFileStream?.id,
                          artikeldatenStreamId: duplicateTarget.artikeldatenFileStream?.id,
                          concurrencyLimit: duplicateTarget.concurrencyLimit ?? undefined,
                          lockout: duplicateTarget.lockout,
                        }
                        await duplicateCommonAssortment(fields, duplicateTarget.commonAssortmentId)
                        setDuplicateTarget(undefined)
                        refresh()
                      }}
                    />
                  </Dialog>
                ) : null}
                {deletionCandidate ? (
                  <Dialog onClose={() => setDeleteCandidate(undefined)} shouldCloseOnOverlayClick={false}>
                    <ConfirmDeletionDialog
                      entityName={deletionCandidate[0]}
                      onConfirm={async () => {
                        await deleteCommonAssortment(deletionCandidate[1])
                        setDeleteCandidate(undefined)
                        refresh()
                      }}
                      onClose={async () => setDeleteCandidate(undefined)}
                    />
                  </Dialog>
                ) : null}
              </div>
            </React.Fragment>
          )
        },
      },
      commonsWithAssortmentConflicts,
    )
  )
}
export default CommonAssortmentOverview

function statusCountsForType(
  type: string,
  commonId: string,
  typeAndStatusCounts: Record<string, number>,
): [UiStatus, number, string][] {
  return Object.entries(typeAndStatusCounts)
    .map(([key, count]) => {
      const [fileType, statusFromBackend] = key.split(':')
      if (fileType !== type) return null
      const uiStatus = mapBackendStatusToUiStatus(statusFromBackend)
      return [uiStatus, count, `/installations?commonId=${commonId}&fileStatus=${uiStatus}&assortmentType=${type}`] as [
        UiStatus,
        number,
        string,
      ]
    })
    .filter((item): item is [UiStatus, number, string] => item !== null)
}
